import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import HowItWorks from "../components/HowItWorks";
import SnapshotScore from "../components/SnapshotScore";
import PricingSection from "../components/PricingSection";
import Footer from "../components/Footer";
import LaunchCalendar from "../components/LaunchCalendar";
import LaunchModal from "../components/LaunchModal";
import CosmicContentSlider from "../components/CosmicContentSlider";
import FloatingNewsletterCapture from "../components/FloatingNewsletterCapture";
import "../components/HowItWorksStyles.css";
import "../components/HeroStyles.css";
import { trackPageView, trackEvent } from "../services/analytics";
import { trackUserInteraction } from "../services/backendAnalytics";

// Define Launch interface to match what LaunchModal expects
interface Launch {
  id: string;
  name: string;
  provider: string;
  launchTime: string;
  launchSite: string;
  missionType: string;
  status: 'GO' | 'HOLD' | 'TBD' | 'SCRUB';
  providerLogo?: string;
  launchPad?: string;
  description?: string; // Make optional since LaunchCalendar doesn't provide it
  streamUrl?: string;
  weatherGo?: number;
  vehicleType?: string;
  missionPatch?: string;
  payload?: string;
  orbit?: string;
}

const LandingPage: React.FC = () => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLaunch, setSelectedLaunch] = useState<Launch | null>(null);

  useEffect(() => {
    // Track page view
    trackPageView('/');
    trackEvent('page_view', { page: 'landing' });
    trackUserInteraction('landing_page', 'view');

    // Track scroll depth
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercentage = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercentage > maxScroll) {
        maxScroll = scrollPercentage;
        if (scrollPercentage > 25 && scrollPercentage <= 50) {
          trackEvent('scroll_depth', { depth: '25%' });
        } else if (scrollPercentage > 50 && scrollPercentage <= 75) {
          trackEvent('scroll_depth', { depth: '50%' });
        } else if (scrollPercentage > 75) {
          trackEvent('scroll_depth', { depth: '75%' });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCalendarOpen = () => {
    setShowCalendar(true);
    trackEvent('calendar', { action: 'open' });
    trackUserInteraction('launch_calendar', 'open');
  };

  const handleSelectLaunch = (launch: Launch) => {
    // Add default description if missing
    const launchWithDefaults = {
      ...launch,
      description: launch.description || `${launch.name} mission launching from ${launch.launchSite}`,
    };
    setSelectedLaunch(launchWithDefaults);
    setShowCalendar(false);
    trackEvent('launch', { 
      action: 'select',
      launchId: launch.id,
      launchName: launch.name
    });
    trackUserInteraction('launch', 'select', {
      launchId: launch.id,
      launchName: launch.name
    });
  };

  const handleCloseModal = () => {
    setSelectedLaunch(null);
    trackEvent('launch_modal', { action: 'close' });
    trackUserInteraction('launch_modal', 'close');
  };

  return (
    <div className="landing-page">
      <Header />
      <main>
        <Hero 
          onCalendarOpen={handleCalendarOpen}
          selectedLaunch={selectedLaunch}
          onSelectLaunch={handleSelectLaunch}
          onCloseModal={handleCloseModal}
        />
        
        <CosmicContentSlider />
        
        <HowItWorks />
        <PricingSection />
        <SnapshotScore />
      </main>
      <Footer />
      {showCalendar && (
        <LaunchCalendar
          onSelectLaunch={handleSelectLaunch}
          onClose={() => {
            setShowCalendar(false);
            trackEvent('calendar', { action: 'close' });
          }}
        />
      )}
      {selectedLaunch && (
        <LaunchModal
          launch={selectedLaunch}
          onClose={handleCloseModal}
        />
      )}
      <FloatingNewsletterCapture />
    </div>
  );
};

export default LandingPage;